import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { inventarioApi, type InventarioParams } from '@/services/api'
import type { InventarioRow } from '@/types'
import { useAllowedEmpresas } from '@/hooks/useAllowedEmpresas'
import { useIsEmpresaAll } from '@/hooks/useIsEmpresaAll'

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useInventarioData(params?: InventarioParams) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['inventario', params],
    queryFn: () => inventarioApi.getInventario(params),
    staleTime: 5 * 60 * 1000,   // 5 min cache (spec requirement)
    gcTime:    10 * 60 * 1000,
  })

  // CamelCaseMiddleware converts snake_case → camelCase.
  // Force Number() on all numeric fields to guard against Decimal-as-string from Pydantic.
  const rows: InventarioRow[] = useMemo(() => {
    const raw = (data?.data ?? []) as InventarioRow[]
    return raw.map(r => ({
      ...r,
      cantidadInventarioFisico:  Number(r.cantidadInventarioFisico  ?? 0),
      cantidadFisicaReservada:   Number(r.cantidadFisicaReservada   ?? 0),
      cantidadFisicaDisponible:  Number(r.cantidadFisicaDisponible  ?? 0),
      cantidadEnPedido:          Number(r.cantidadEnPedido          ?? 0),
      cantidadOrdenadaReservada: Number(r.cantidadOrdenadaReservada ?? 0),
      cantidadPedidoEnTotal:     Number(r.cantidadPedidoEnTotal     ?? 0),
      cantidadTotalDisponible:   Number(r.cantidadTotalDisponible   ?? 0),
    }))
  }, [data])

  // ── KPIs ─────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    if (!rows.length) return { skusConStock: 0, skusEnCero: 0, totalSkus: 0, totalUnidades: 0 }

    let skusConStock  = 0
    let skusEnCero    = 0
    let totalUnidades = 0

    for (const r of rows) {
      if (r.cantidadFisicaDisponible > 0) skusConStock++
      else skusEnCero++
      totalUnidades += r.cantidadFisicaDisponible
    }

    return { skusConStock, skusEnCero, totalSkus: rows.length, totalUnidades }
  }, [rows])

  // ── Unique values for filter selects ─────────────────────────────────────
  // Use JWT roles as the source of truth so the empresa selector always shows
  // all accessible companies, even if TIDS (or another) has no inventory rows yet.
  const allEmpresas  = useAllowedEmpresas()
  const isEmpresaAll = useIsEmpresaAll()
  const allAlmacenes = useMemo(
    () => [...new Set(rows.map(r => r.almacen ?? '').filter(Boolean))].sort(),
    [rows],
  )
  const allLineas = useMemo(
    () => [...new Set(rows.map(r => r.linea ?? '').filter(Boolean))].sort(),
    [rows],
  )
  const allFamilias = useMemo(
    () => [...new Set(rows.map(r => r.familia ?? '').filter(Boolean))].sort(),
    [rows],
  )

  return {
    rows,
    isLoading,
    isError,
    refetch,
    kpis,
    allEmpresas,
    isEmpresaAll,
    allAlmacenes,
    allLineas,
    allFamilias,
    updatedAt: data?.updatedAt,
  }
}
